/**
 * The dashboard: search, category pills, lineage filter, monastery grid.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import MonasteryCard, { CATEGORY_ICONS } from '../components/MonasteryCard';
import FilterSheet from '../components/FilterSheet';
import { MonasteryGridSkeleton, EmptyState } from '../components/ui';
import { useBooking } from '../context/BookingContext';
import { api } from '../lib/api';
import { trackEvent } from '../lib/consent';

export default function Explore() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeLineages, toggleLineage, clearLineages } = useBooking();

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'All');
  const [categories, setCategories] = useState(['All']);
  const [lineages, setLineages] = useState([]);
  const [monasteries, setMonasteries] = useState(null);
  const [error, setError] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const seeded = useRef(false);
  useEffect(() => {
    const fromUrl = searchParams.get('lineage');
    if (fromUrl && !seeded.current) {
      seeded.current = true;
      // Apply the deep-linked tradition, but don't pop the sheet open on
      // load — the filter control's badge already shows it is active.
      if (!activeLineages.includes(fromUrl)) toggleLineage(fromUrl);
    }
  }, [searchParams, activeLineages, toggleLineage]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([api.categories(controller.signal), api.lineages(controller.signal)])
      .then(([c, l]) => {
        setCategories(c.categories);
        setLineages(l.lineages);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setError('');
      api
        .monasteries(
          { q: query, category, lineage: activeLineages.join(',') },
          controller.signal
        )
        .then(({ monasteries: results }) => {
          setMonasteries(results);
          // Tracked here, not before the request: this fires for a search
          // that actually ran and returned, once per settled query, rather
          // than on every keystroke and every aborted fetch.
          if (query) {
            trackEvent('search', { search_term: query, results_count: results.length });
          }
          trackEvent('view_item_list', {
            item_list_name: 'explore',
            item_count: results.length,
            category: category === 'All' ? undefined : category,
            lineage: activeLineages.length ? activeLineages.join(',') : undefined,
          });
        })
        .catch((err) => {
          if (err.name !== 'AbortError') setError(err.message);
        });
    }, query ? 220 : 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, category, activeLineages]);

  useEffect(() => {
    const next = {};
    if (query) next.q = query;
    if (category !== 'All') next.category = category;
    if (activeLineages.length) next.lineage = activeLineages.join(',');
    setSearchParams(next, { replace: true });
  }, [query, category, activeLineages, setSearchParams]);

  const isFiltered = activeLineages.length > 0;
  const count = monasteries?.length ?? 0;

  const searchPlaceholder =
    monasteries == null
      ? t('explore.searchIdle', 'Search monasteries')
      : t('explore.searchWithCount', {
          count,
          defaultValue: `Search ${count} ${count === 1 ? 'monastery' : 'monasteries'}`,
        });

  return (
    <div className="shell">
      <header className="explore-head stack">
        <p className="eyebrow">{t('explore.eyebrow', 'Explore')}</p>
      </header>

      <div className="explore-toolbar">
        <div className="search-box">
          <Icon name="search" size={19} />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={t('explore.searchAria', 'Search monasteries')}
          />
          {query && (
            <button
              type="button"
              className="search-box__clear"
              onClick={() => setQuery('')}
              aria-label={t('explore.clearSearch', 'Clear search')}
            >
              <Icon name="close" size={17} />
            </button>
          )}
          <button
            type="button"
            className={`search-box__filter${isFiltered ? ' is-active' : ''}`}
            onClick={() => setFilterOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={filterOpen}
            aria-label={
              isFiltered
                ? t('explore.filterOpenActive', {
                    count: activeLineages.length,
                    defaultValue: `Filters — ${activeLineages.length} active`,
                  })
                : t('explore.filterOpen', 'Filters')
            }
          >
            <Icon name="options" size={18} />
            {isFiltered && (
              <span className="search-box__filter-badge">{activeLineages.length}</span>
            )}
          </button>
        </div>

        <div className="pill-row" role="group" aria-label={t('explore.filterAria', 'Filter by offering type')}>
          {categories.map((name) => (
            <button
              key={name}
              type="button"
              className={`pill${category === name ? ' is-selected' : ''}`}
              aria-pressed={category === name}
              onClick={() => { setCategory(name); trackEvent('filter_category', { category: name }); }}
            >
              <span className="pill__layer pill__layer--settled" />
              <span className="pill__layer pill__layer--raised" />
              <span className="pill__content">
                {name !== 'All' && <Icon name={CATEGORY_ICONS[name] || 'lotus'} size={14} />}
                {name === 'All' ? t('explore.allFilter', 'All offerings') : name}
              </span>
            </button>
          ))}
        </div>

        {isFiltered && (
          <p className="filter-note">
            <Icon name="lotus" size={15} />
            {t('explore.hiddenNote', {
              visible: activeLineages.length,
              total: lineages.length,
              defaultValue: `Showing ${activeLineages.length} of ${lineages.length} traditions — some monasteries are hidden.`,
            })}
            <button type="button" onClick={clearLineages}>{t('explore.showAll', 'Show all')}</button>
          </p>
        )}
      </div>

      <div style={{ paddingBlock: 'var(--space-2xl) var(--space-5xl)' }}>
        {error ? (
          <EmptyState icon="alert" title={t('explore.loadError', 'Could not load the monasteries')}>
            {error}
          </EmptyState>
        ) : monasteries == null ? (
          <MonasteryGridSkeleton count={6} />
        ) : count === 0 ? (
          <EmptyState
            icon={isFiltered ? 'lotus' : 'compass'}
            title={
              isFiltered
                ? t('explore.emptyHiddenTitle', 'Nothing matches these traditions')
                : t('explore.emptySearchTitle', 'No monasteries found')
            }
            action={
              isFiltered ? (
                <button type="button" className="btn btn--primary" onClick={clearLineages}>
                  {t('explore.showAllTraditions', 'Show all traditions')}
                </button>
              ) : (
                <Link
                  to="/explore"
                  className="btn btn--ghost"
                  onClick={() => { setQuery(''); setCategory('All'); }}
                >
                  {t('explore.clearSearchBtn', 'Clear the search')}
                </Link>
              )
            }
          >
            {isFiltered
              ? t('explore.emptyHiddenBody', 'Some monasteries are hidden by your lineage filter. Clear the filter to see everything.')
              : t('explore.emptySearchBody', 'Try a different search, or a different kind of offering.')}
          </EmptyState>
        ) : (
          <div className="grid-cards">
            {monasteries.map((monastery) => (
              <MonasteryCard key={monastery.id} monastery={monastery} />
            ))}
          </div>
        )}
      </div>

      <FilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        lineages={lineages}
        activeLineages={activeLineages}
        toggleLineage={toggleLineage}
        clearLineages={clearLineages}
        resultCount={count}
      />
    </div>
  );
}
